import React, { useState, useEffect } from 'react';
import { aggregateService } from '../services/aggregateService';
import { showToast } from '../utils/toast';

const Aggregate = () => {
    // API Key state
    const [apiKey, setApiKey] = useState("");
    const [apiKeyValid, setApiKeyValid] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [initializing, setInitializing] = useState(true);

    // Data state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [availableSymbols, setAvailableSymbols] = useState([]);
    const [availableTimeframes, setAvailableTimeframes] = useState([]);
    const [selectedSymbol, setSelectedSymbol] = useState('USDINR');
    const [selectedTimeframe, setSelectedTimeframe] = useState('day');
    const [aggregateData, setAggregateData] = useState(null);
    const [responseTime, setResponseTime] = useState(null);
    const [dataAge, setDataAge] = useState(null);
    const [recordCount, setRecordCount] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [dataSource, setDataSource] = useState(null);

    // Manual refetch state
    const [refetchLoading, setRefetchLoading] = useState(false);

    // Timeframe display names
    const timeframeLabels = {
        'minute': 'Minute',
        'hour': 'Hour', 
        'day': 'Day',
        'week': 'Week',
        'month': 'Month',
        'quarter': 'Quarter',
        'year': 'Year'
    };

    // Load API key from localStorage on component mount
    useEffect(() => {
        const initializeApiKey = async () => {
            const savedApiKey = localStorage.getItem("aggregate_api_key");
            if (savedApiKey) {
                setApiKey(savedApiKey);
                await validateApiKey(savedApiKey);
            }
            setInitializing(false);
        };

        initializeApiKey();
    }, []);

    // Validate API key by fetching available symbols
    const validateApiKey = async (key) => {
        if (!key || key.trim() === "") {
            setApiKeyValid(false);
            setError("Please enter your API key");
            return false;
        }

        try {
            setLoading(true);
            setError("");

            // Test API key with available symbols request
            const response = await aggregateService.getAvailableSymbols(key);

            if (response.success) {
                setApiKeyValid(true);
                setAvailableSymbols(response.symbols || []);
                setAvailableTimeframes(response.timeframes || []);
                setUserInfo({ email: response.user });
                localStorage.setItem("aggregate_api_key", key);
                showToast.success("API key validated successfully!");
                return true;
            } else {
                setApiKeyValid(false);
                setError(response.error || "Invalid API key");
                localStorage.removeItem("aggregate_api_key");
                showToast.error("Invalid API key");
                return false;
            }
        } catch (error) {
            console.error("API key validation error:", error);
            setApiKeyValid(false);
            setError("Failed to validate API key");
            localStorage.removeItem("aggregate_api_key");
            showToast.error("Failed to validate API key");
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Handle API key submission
    const handleApiKeySubmit = async (e) => {
        e.preventDefault();
        await validateApiKey(apiKey);
    };

    // Clear API key
    const clearApiKey = () => {
        setApiKey("");
        setApiKeyValid(false);
        setUserInfo(null);
        setAvailableSymbols([]);
        setAvailableTimeframes([]);
        setAggregateData(null);
        setSelectedSymbol('USDINR');
        setSelectedTimeframe('day');
        setError("");
        setLoading(false);
        localStorage.removeItem("aggregate_api_key");
        showToast.info("API key cleared");
    };

    // Fetch aggregate data for selected symbol and timeframe
    const fetchAggregateData = async (symbol = selectedSymbol, timeframe = selectedTimeframe) => {
        if (!apiKeyValid || !apiKey || !symbol || !timeframe) {
            setError("Please provide a valid API key and select symbol/timeframe");
            return;
        }

        try {
            setLoading(true);
            setError("");

            const startTime = Date.now();
            const response = await aggregateService.getAggregateData(symbol, timeframe, apiKey);

            if (response.success) {
                setAggregateData(response.data);
                setResponseTime(Date.now() - startTime);
                setDataAge(response.dataAgeSeconds);
                setRecordCount(response.recordCount);
                setDateRange(response.dateRange);
                setDataSource(response.source);
                setUserInfo({ email: response.user });
                showToast.success(`Data for ${symbol} (${timeframe}) fetched successfully!`);
            } else {
                setError(response.error || "Failed to fetch aggregate data");
                setAggregateData(null);
                
                // Handle authentication errors
                if (response.error?.includes("Authentication") || response.error?.includes("API key")) {
                    setApiKeyValid(false);
                    localStorage.removeItem("aggregate_api_key");
                }
            }
        } catch (error) {
            console.error("Error fetching aggregate data:", error);
            setError("Network error. Please try again.");
            setAggregateData(null);
        } finally {
            setLoading(false);
        }
    };

    // Manual refetch all data (admin function)
    const handleManualRefetch = async () => {
        if (!apiKeyValid || !apiKey) {
            showToast.error("Please provide a valid API key first");
            return;
        }

        try {
            setRefetchLoading(true);
            
            // Call the actual manual refetch API
            const response = await aggregateService.triggerManualRefetch(apiKey);
            
            if (response.success) {
                showToast.success("Manual refetch started successfully!");
                showToast.info(`Estimated completion time: ${response.estimatedTime}`);
                showToast.info("You can continue using cached data while fresh data is being fetched.");
            } else {
                showToast.error(response.error || "Failed to trigger manual refetch");
                
                // Handle authentication errors
                if (response.error?.includes("Authentication") || response.error?.includes("API key")) {
                    setApiKeyValid(false);
                    localStorage.removeItem("aggregate_api_key");
                }
            }
        } catch (error) {
            console.error("Error triggering manual refetch:", error);
            showToast.error("Failed to trigger manual refetch. Please try again.");
        } finally {
            setRefetchLoading(false);
        }
    };

    // Handle symbol change
    const handleSymbolChange = (symbol) => {
        setSelectedSymbol(symbol);
        if (symbol && selectedTimeframe) {
            fetchAggregateData(symbol, selectedTimeframe);
        }
    };

    // Handle timeframe change
    const handleTimeframeChange = (timeframe) => {
        setSelectedTimeframe(timeframe);
        if (selectedSymbol && timeframe) {
            fetchAggregateData(selectedSymbol, timeframe);
        }
    };

    // Refresh current data
    const handleRefresh = () => {
        if (selectedSymbol && selectedTimeframe) {
            fetchAggregateData(selectedSymbol, selectedTimeframe);
        }
    };

    // Format functions
    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleString();
    };

    // Show loading during initialization
    if (initializing) {
        return (
            <div className="container py-4">
                <div className="card shadow">
                    <div className="card-body text-center py-5">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5>Initializing...</h5>
                        <p className="text-muted">Checking for saved API key...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show API key form if not authenticated
    if (!apiKeyValid) {
        return (
            <div className="container py-4">
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <div className="card shadow">
                            <div className="card-header bg-primary text-white text-center">
                                <h4 className="mb-0">
                                    <i className="bi bi-key me-2"></i>
                                    API Authentication Required
                                </h4>
                            </div>
                            <div className="card-body">
                                <p className="text-muted text-center mb-4">
                                    Please enter your API key to access forex aggregate historical data.
                                </p>

                                <form onSubmit={handleApiKeySubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="apiKey" className="form-label fw-bold">
                                            API Key
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="apiKey"
                                            placeholder="Enter your API key"
                                            value={apiKey}
                                            onChange={(e) => setApiKey(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                        <div className="form-text">
                                            Your API key is required to authenticate and track your usage.
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="alert alert-danger">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100"
                                        disabled={loading || !apiKey.trim()}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Validating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-shield-check me-2"></i>
                                                Validate & Continue
                                            </>
                                        )}
                                    </button>
                                </form>

                                <div className="mt-4 p-3 bg-light rounded">
                                    <h6 className="fw-bold mb-2">
                                        <i className="bi bi-info-circle me-2"></i>
                                        How to get your API Key:
                                    </h6>
                                    <ol className="mb-0 small">
                                        <li>Go to your account dashboard</li>
                                        <li>Navigate to API Settings</li>
                                        <li>Copy your API key</li>
                                        <li>Paste it in the field above</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4">
            <div className="card shadow">
                <div className="card-header bg-primary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="mb-1">Forex Aggregate Historical Data</h3>
                            <small>
                                Historical data across multiple timeframes - Updated daily at midnight |
                                {userInfo && ` User: ${userInfo.email}`}
                            </small>
                        </div>
                        <button
                            className="btn btn-outline-light btn-sm"
                            onClick={clearApiKey}
                            title="Change API Key"
                        >
                            <i className="bi bi-key"></i>
                        </button>
                    </div>
                </div>
                
                <div className="card-body">
                    {/* Performance Stats */}
                    {(responseTime || dataAge !== null || recordCount || dateRange) && (
                        <div className="alert alert-info mb-4">
                            <div className="row text-center">
                                <div className="col-md-3">
                                    {responseTime && (
                                        <span><strong>Response:</strong> {responseTime}ms</span>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    {dataAge !== null && (
                                        <span><strong>Data Age:</strong> {Math.floor(dataAge / 3600)}h {Math.floor((dataAge % 3600) / 60)}m</span>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    {recordCount > 0 && (
                                        <span><strong>Records:</strong> {recordCount.toLocaleString()}</span>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    {dateRange && (
                                        <span><strong>Range:</strong> {formatDate(dateRange.from)} - {formatDate(dateRange.to)}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="alert alert-danger mb-4">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    )}

                    {/* Controls */}
                    <div className="row mb-4">
                        {/* Symbol Selection */}
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Select Symbol:</label>
                            <select
                                className="form-select"
                                value={selectedSymbol}
                                onChange={(e) => handleSymbolChange(e.target.value)}
                                disabled={loading}
                            >
                                {availableSymbols.map(symbol => (
                                    <option key={symbol} value={symbol}>{symbol}</option>
                                ))}
                            </select>
                        </div>

                        {/* Timeframe Selection */}
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Select Timeframe:</label>
                            <select
                                className="form-select"
                                value={selectedTimeframe}
                                onChange={(e) => handleTimeframeChange(e.target.value)}
                                disabled={loading}
                            >
                                {availableTimeframes.map(tf => (
                                    <option key={tf} value={tf}>{timeframeLabels[tf] || tf}</option>
                                ))}
                            </select>
                        </div>

                        {/* Actions */}
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Actions:</label>
                            <div className="d-flex gap-2 flex-wrap">
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleRefresh} 
                                    disabled={loading || !selectedSymbol || !selectedTimeframe}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-clockwise me-2"></i>
                                            Get Data
                                        </>
                                    )}
                                </button>

                                <button 
                                    className="btn btn-warning"
                                    onClick={handleManualRefetch}
                                    disabled={refetchLoading}
                                    title="Trigger manual data refetch (admin function)"
                                >
                                    {refetchLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Fetching...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-cloud-download me-2"></i>
                                            Manual Refetch
                                        </>
                                    )}
                                </button>
                            </div>
                            <small className="text-muted">
                                Data is automatically updated daily at midnight (12:00 AM IST)
                            </small>
                        </div>
                    </div>

                    {/* Available Timeframes Display */}
                    {availableTimeframes.length > 0 && (
                        <div className="mb-4">
                            <h6>Available Timeframes ({availableTimeframes.length}):</h6>
                            <div className="d-flex flex-wrap gap-2">
                                {availableTimeframes.map(tf => (
                                    <span 
                                        key={tf} 
                                        className={`badge ${tf === selectedTimeframe ? 'bg-primary' : 'bg-secondary'}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleTimeframeChange(tf)}
                                    >
                                        {timeframeLabels[tf] || tf}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Data Display */}
                    <div className="mt-4">
                        {aggregateData ? (
                            <div>
                                <div className="d-flex align-items-center mb-3">
                                    <h4 className="h5 mb-0">
                                        {selectedSymbol} - {timeframeLabels[selectedTimeframe]} Data
                                    </h4>
                                    <span className="badge bg-success ms-2">Cached Data</span>
                                    {recordCount > 0 && (
                                        <span className="badge bg-info ms-2">{recordCount.toLocaleString()} Records</span>
                                    )}
                                    {dataSource && (
                                        <span className="badge bg-secondary ms-2">{dataSource}</span>
                                    )}
                                </div>
                                
                                {/* Data Summary */}
                                {aggregateData.results && aggregateData.results.length > 0 && (
                                    <div className="row mb-3">
                                        <div className="col-md-12">
                                            <div className="card bg-light">
                                                <div className="card-body">
                                                    <h6>Latest Data Point:</h6>
                                                    <div className="row">
                                                        <div className="col-md-2">
                                                            <strong>Open:</strong> {aggregateData.results[0]?.o}
                                                        </div>
                                                        <div className="col-md-2">
                                                            <strong>High:</strong> {aggregateData.results[0]?.h}
                                                        </div>
                                                        <div className="col-md-2">
                                                            <strong>Low:</strong> {aggregateData.results[0]?.l}
                                                        </div>
                                                        <div className="col-md-2">
                                                            <strong>Close:</strong> {aggregateData.results[0]?.c}
                                                        </div>
                                                        <div className="col-md-2">
                                                            <strong>Volume:</strong> {aggregateData.results[0]?.v}
                                                        </div>
                                                        <div className="col-md-2">
                                                            <strong>Time:</strong> {formatTime(aggregateData.results[0]?.t)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Raw Data */}
                                <div className="bg-light p-3 rounded border" style={{ maxHeight: '500px', overflow: 'auto' }}>
                                    <pre className="mb-0" style={{ fontSize: '0.875rem' }}>
                                        {JSON.stringify(aggregateData, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <i className="bi bi-bar-chart" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                <h5 className="mt-3 text-muted">No Data Available</h5>
                                <p className="text-muted">
                                    Select a symbol and timeframe, then click "Get Data" to view historical aggregate data.
                                </p>
                                <p className="text-muted small">
                                    Data is updated daily at midnight. If no data is available, it may still be processing.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Aggregate;